/* *
 * =============================================================================
 * AscendDraft Admin Pricing Management System
 * =============================================================================
 * 
 * Admin interface for managing service prices, discounts, rush fees, and coupons.
 * Provides real-time updates and syncs with backend.
 * 
 * FEATURES:
 * - Update service prices
 * - Enable/disable services
 * - Manage rush delivery fees
 * - Configure discount percentages
 * - Create and manage coupon codes
 * - Category-based discount management
 * - Sync with finance backend
 * 
 * @version 1.0.0 */

'use strict';

// ==========================================================================
// CONFIGURATION
// ==========================================================================
  
  const CONFIG = {
    storageKey: 'ascend_pricing_config',
    // Catalog reads use the public pricing endpoint; writes go through the main admin app
    apiBase: window.ASCEND_API_BASE || (
      ['localhost', '127.0.0.1'].includes(window.location.hostname)
        ? 'http://localhost:5001/api'
        : 'https://api.ascenddraft.com/api'
    ),
  };

  // ==========================================================================
  // DEFAULT PRICING DATA
  // ==========================================================================
  
  let pricingConfig = {
    version: 1,
    lastUpdated: null,
    
    // Service status (enabled/disabled)
    serviceStatus: {},
    
    // Price overrides (null = use default)
    priceOverrides: {},
    
    // Rush delivery configuration
    rushDelivery: {
      enabled: true,
      tiers: {
        standard: { enabled: true, multiplier: 1.0, label: 'Standard (5-7 days)' },
        express: { enabled: true, multiplier: 1.10, label: 'Express (+10%)' },
        rush: { enabled: true, multiplier: 1.15, label: 'Rush (+15%)' },
        emergency: { enabled: true, multiplier: 1.25, label: 'Emergency (+25%)' }
      }
    },
    
    // Discount configuration
    discounts: {
      sameCategoryDiscount: { enabled: true, percent: 10, minItems: 2 },
      crossCategoryDiscount: { enabled: true, percent: 5 },
      membershipDiscounts: {
        gold: { enabled: true, percent: 4 },
        silver: { enabled: true, percent: 2 },
        bronze: { enabled: true, percent: 1 },
        free: { enabled: true, percent: 0 }
      }
    },
    
    // Coupon codes
    coupons: {}
  };

  // ==========================================================================
  // INITIALIZATION
  // ==========================================================================
  
  async function init() {
    // Load from storage first
    loadFromStorage();
    
    // Try to sync with backend
    await syncWithBackend();
    
    console.log('[AdminPricing] Initialized');
  }

  // ==========================================================================
  // STORAGE
  // ==========================================================================
  
  function loadFromStorage() {
    try {
      const stored = localStorage.getItem(CONFIG.storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        pricingConfig = { ...pricingConfig, ...parsed };
      }
    } catch (e) {
      console.warn('[AdminPricing] Load error:', e);
    }
  }

  function saveToStorage() {
    try {
      pricingConfig.lastUpdated = new Date().toISOString();
      localStorage.setItem(CONFIG.storageKey, JSON.stringify(pricingConfig));
    } catch (e) {
      console.warn('[AdminPricing] Save error:', e);
    }
  }

  // ==========================================================================
  // BACKEND SYNC
  // ==========================================================================
  
  async function syncWithBackend() {
    try {
      // Read the live service catalog from the pricing endpoint (MongoDB-backed)
      const response = await fetch(`${CONFIG.apiBase}/pricing/catalog`, {
        signal: AbortSignal.timeout(6000),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (!data.success) throw new Error('API returned success:false');
      // Hydrate local rush and discount config from catalog metadata if available
      if (data.data?.rushConfig) {
        pricingConfig.rushDelivery = { ...pricingConfig.rushDelivery, ...data.data.rushConfig };
      }
      if (data.data?.discountConfig) {
        pricingConfig.discounts = { ...pricingConfig.discounts, ...data.data.discountConfig };
      }
      saveToStorage();
    } catch (e) {
      console.warn('[AdminPricing] Sync failed:', e.message);
    }
  }

  async function pushToBackend() {
    // Price/service changes must go through the main AscendDraft admin app (/admin/services).
    // This website-side config only manages local rush/discount display preferences.
    saveToStorage();
    return true;
  }

  // ==========================================================================
  // SERVICE MANAGEMENT
  // ==========================================================================
  
  function enableService(serviceId) {
    pricingConfig.serviceStatus[serviceId] = true;
    pushToBackend();
    emitChange('service-enabled', serviceId);
  }

  function disableService(serviceId) {
    pricingConfig.serviceStatus[serviceId] = false;
    pushToBackend();
    emitChange('service-disabled', serviceId);
  }

  function isServiceEnabled(serviceId) {
    return pricingConfig.serviceStatus[serviceId] !== false;
  }

  function setServicePrice(serviceId, price) {
    pricingConfig.priceOverrides[serviceId] = price;
    pushToBackend();
    emitChange('price-updated', serviceId);
  }

  function getServicePrice(serviceId, defaultPrice) {
    const override = pricingConfig.priceOverrides[serviceId];
    return override !== null && override !== undefined ? override : defaultPrice;
  }

  function clearPriceOverride(serviceId) {
    delete pricingConfig.priceOverrides[serviceId];
    pushToBackend();
    emitChange('price-cleared', serviceId);
  }

  // ==========================================================================
  // RUSH DELIVERY MANAGEMENT
  // ==========================================================================
  
  function setRushEnabled(enabled) {
    pricingConfig.rushDelivery.enabled = enabled;
    pushToBackend();
    emitChange('rush-toggled', enabled);
  }

  function setRushTierEnabled(tier, enabled) {
    if (pricingConfig.rushDelivery.tiers[tier]) {
      pricingConfig.rushDelivery.tiers[tier].enabled = enabled;
      pushToBackend();
      emitChange('rush-tier-toggled', { tier, enabled });
    }
  }

  function setRushTierMultiplier(tier, multiplier) {
    if (pricingConfig.rushDelivery.tiers[tier]) {
      pricingConfig.rushDelivery.tiers[tier].multiplier = multiplier;
      pushToBackend();
      emitChange('rush-multiplier-updated', { tier, multiplier });
    }
  }

  function getRushConfig() {
    return { ...pricingConfig.rushDelivery };
  }

  function isRushTierEnabled(tier) {
    return pricingConfig.rushDelivery.enabled && 
           pricingConfig.rushDelivery.tiers[tier]?.enabled !== false;
  }

  // ==========================================================================
  // DISCOUNT MANAGEMENT
  // ==========================================================================
  
  function setDiscountConfig(type, config) {
    if (type === 'sameCategory') {
      pricingConfig.discounts.sameCategoryDiscount = {
        ...pricingConfig.discounts.sameCategoryDiscount,
        ...config
      };
    } else if (type === 'crossCategory') {
      pricingConfig.discounts.crossCategoryDiscount = {
        ...pricingConfig.discounts.crossCategoryDiscount,
        ...config
      };
    } else if (type === 'membership' && config.tier) {
      pricingConfig.discounts.membershipDiscounts[config.tier] = {
        ...pricingConfig.discounts.membershipDiscounts[config.tier],
        ...config
      };
    }
    
    pushToBackend();
    emitChange('discount-updated', type);
  }

  function getDiscountConfig() {
    return { ...pricingConfig.discounts };
  }

  function getSameCategoryDiscount() {
    const config = pricingConfig.discounts.sameCategoryDiscount;
    return config.enabled ? config.percent : 0;
  }

  function getCrossCategoryDiscount() {
    const config = pricingConfig.discounts.crossCategoryDiscount;
    return config.enabled ? config.percent : 0;
  }

  function getMembershipDiscount(tier) {
    const config = pricingConfig.discounts.membershipDiscounts[tier];
    return config?.enabled ? config.percent : 0;
  }

  // ==========================================================================
  // COUPON CODE MANAGEMENT
  // ==========================================================================
  
  function createCoupon(code, config) {
    const couponCode = code.toUpperCase().trim();
    
    pricingConfig.coupons[couponCode] = {
      code: couponCode,
      discount: config.discount || 5, // Default 5%
      type: config.type || 'percent', // percent or fixed
      enabled: true,
      usageLimit: config.usageLimit || null, // null = unlimited
      usageCount: 0,
      validFrom: config.validFrom || new Date().toISOString(),
      validUntil: config.validUntil || null, // null = no expiry
      applicableServices: config.applicableServices || [], // empty = all
      applicableCategories: config.applicableCategories || [], // empty = all
      minPurchase: config.minPurchase || 0,
      createdAt: new Date().toISOString(),
      createdBy: config.createdBy || 'admin'
    };
    
    pushToBackend();
    emitChange('coupon-created', couponCode);
    
    return pricingConfig.coupons[couponCode];
  }

  function updateCoupon(code, updates) {
    const couponCode = code.toUpperCase().trim();
    
    if (!pricingConfig.coupons[couponCode]) {
      return null;
    }
    
    pricingConfig.coupons[couponCode] = {
      ...pricingConfig.coupons[couponCode],
      ...updates,
      updatedAt: new Date().toISOString()
    };
    
    pushToBackend();
    emitChange('coupon-updated', couponCode);
    
    return pricingConfig.coupons[couponCode];
  }

  function disableCoupon(code) {
    return updateCoupon(code, { enabled: false });
  }

  function enableCoupon(code) {
    return updateCoupon(code, { enabled: true });
  }

  function deleteCoupon(code) {
    const couponCode = code.toUpperCase().trim();
    delete pricingConfig.coupons[couponCode];
    pushToBackend();
    emitChange('coupon-deleted', couponCode);
  }

  function getCoupon(code) {
    const couponCode = code.toUpperCase().trim();
    return pricingConfig.coupons[couponCode] || null;
  }

  function validateCoupon(code, orderDetails = {}) {
    const coupon = getCoupon(code);
    
    if (!coupon) {
      return { valid: false, error: 'Invalid coupon code' };
    }
    
    if (!coupon.enabled) {
      return { valid: false, error: 'Coupon is no longer active' };
    }
    
    const now = new Date();
    
    if (coupon.validFrom && new Date(coupon.validFrom) > now) {
      return { valid: false, error: 'Coupon is not yet valid' };
    }
    
    if (coupon.validUntil && new Date(coupon.validUntil) < now) {
      return { valid: false, error: 'Coupon has expired' };
    }
    
    if (coupon.usageLimit && coupon.usageCount >= coupon.usageLimit) {
      return { valid: false, error: 'Coupon usage limit reached' };
    }
    
    if (coupon.minPurchase && orderDetails.subtotal < coupon.minPurchase) {
      return { valid: false, error: `Minimum purchase of $${coupon.minPurchase} required` };
    }
    
    // Check applicable services
    if (coupon.applicableServices?.length > 0 && orderDetails.serviceIds) {
      const hasApplicable = orderDetails.serviceIds.some(id => 
        coupon.applicableServices.includes(id)
      );
      if (!hasApplicable) {
        return { valid: false, error: 'Coupon not valid for selected services' };
      }
    }
    
    // Check applicable categories
    if (coupon.applicableCategories?.length > 0 && orderDetails.categories) {
      const hasApplicable = orderDetails.categories.some(cat => 
        coupon.applicableCategories.includes(cat)
      );
      if (!hasApplicable) {
        return { valid: false, error: 'Coupon not valid for selected categories' };
      }
    }
    
    return {
      valid: true,
      coupon,
      discount: coupon.discount,
      type: coupon.type
    };
  }

  function applyCoupon(code) {
    const couponCode = code.toUpperCase().trim();
    const coupon = pricingConfig.coupons[couponCode];
    
    if (coupon) {
      coupon.usageCount = (coupon.usageCount || 0) + 1;
      pushToBackend();
    }
  }

  function getAllCoupons() {
    return Object.values(pricingConfig.coupons);
  }

  function getActiveCoupons() {
    const now = new Date();
    return getAllCoupons().filter(coupon => {
      if (!coupon.enabled) return false;
      if (coupon.validUntil && new Date(coupon.validUntil) < now) return false;
      if (coupon.usageLimit && coupon.usageCount >= coupon.usageLimit) return false;
      return true;
    });
  }

  // ==========================================================================
  // EVENTS
  // ==========================================================================
  
  function emitChange(type, data) {
    document.dispatchEvent(new CustomEvent('pricingConfigChanged', {
      detail: { type, data, config: pricingConfig }
    }));
  }

  // ==========================================================================
  // ADMIN UI RENDERER
  // ==========================================================================
  
  function renderAdminPanel(containerId) {
    const container = document.getElementById(containerId);
    if (!container) return;

    container.innerHTML = `
      <div class="admin-pricing-panel">
        <style>
          .admin-pricing-panel {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: #f9fafb;
            border-radius: 12px;
            padding: 24px;
          }
          .admin-section {
            background: white;
            border-radius: 8px;
            padding: 20px;
            margin-bottom: 20px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          }
          .admin-section-title {
            font-size: 18px;
            font-weight: 600;
            color: #111827;
            margin-bottom: 16px;
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .admin-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
            gap: 16px;
          }
          .admin-field {
            margin-bottom: 12px;
          }
          .admin-label {
            display: block;
            font-size: 13px;
            font-weight: 500;
            color: #374151;
            margin-bottom: 6px;
          }
          .admin-input {
            width: 100%;
            padding: 8px 12px;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            font-size: 14px;
          }
          .admin-input:focus {
            outline: none;
            border-color: #3b82f6;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          }
          .admin-toggle {
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .toggle-switch {
            position: relative;
            width: 44px;
            height: 24px;
          }
          .toggle-switch input {
            opacity: 0;
            width: 0;
            height: 0;
          }
          .toggle-slider {
            position: absolute;
            cursor: pointer;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: #d1d5db;
            transition: .3s;
            border-radius: 24px;
          }
          .toggle-slider:before {
            position: absolute;
            content: "";
            height: 18px;
            width: 18px;
            left: 3px;
            bottom: 3px;
            background-color: white;
            transition: .3s;
            border-radius: 50%;
          }
          input:checked + .toggle-slider {
            background-color: #3b82f6;
          }
          input:checked + .toggle-slider:before {
            transform: translateX(20px);
          }
          .admin-btn {
            padding: 8px 16px;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            font-weight: 500;
            transition: all 0.2s;
          }
          .admin-btn-primary {
            background: #3b82f6;
            color: white;
          }
          .admin-btn-primary:hover {
            background: #2563eb;
          }
          .admin-btn-danger {
            background: #ef4444;
            color: white;
          }
          .admin-btn-danger:hover {
            background: #dc2626;
          }
          .coupon-table {
            width: 100%;
            border-collapse: collapse;
          }
          .coupon-table th,
          .coupon-table td {
            padding: 10px;
            text-align: left;
            border-bottom: 1px solid #e5e7eb;
          }
          .coupon-table th {
            background: #f9fafb;
            font-weight: 600;
            font-size: 12px;
            text-transform: uppercase;
          }
          .status-badge {
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 11px;
            font-weight: 600;
          }
          .status-active { background: #d1fae5; color: #065f46; }
          .status-inactive { background: #fee2e2; color: #991b1b; }
        </style>

        <!-- Discount Configuration -->
        <div class="admin-section">
          <h3 class="admin-section-title">🏷️ Discount Settings</h3>
          <div class="admin-grid">
            <div class="admin-field">
              <label class="admin-label">Same Category Discount (%)</label>
              <div class="admin-toggle">
                <label class="toggle-switch">
                  <input type="checkbox" id="samecat-enabled" 
                    ${pricingConfig.discounts.sameCategoryDiscount.enabled ? 'checked' : ''}
                    onchange="AdminPricing.setDiscountConfig('sameCategory', {enabled: this.checked})">
                  <span class="toggle-slider"></span>
                </label>
                <input type="number" class="admin-input" id="samecat-percent" 
                  value="${pricingConfig.discounts.sameCategoryDiscount.percent}"
                  style="width: 80px;"
                  onchange="AdminPricing.setDiscountConfig('sameCategory', {percent: parseFloat(this.value)})">
              </div>
            </div>
            <div class="admin-field">
              <label class="admin-label">Cross Category Discount (%)</label>
              <div class="admin-toggle">
                <label class="toggle-switch">
                  <input type="checkbox" id="crosscat-enabled"
                    ${pricingConfig.discounts.crossCategoryDiscount.enabled ? 'checked' : ''}
                    onchange="AdminPricing.setDiscountConfig('crossCategory', {enabled: this.checked})">
                  <span class="toggle-slider"></span>
                </label>
                <input type="number" class="admin-input" id="crosscat-percent"
                  value="${pricingConfig.discounts.crossCategoryDiscount.percent}"
                  style="width: 80px;"
                  onchange="AdminPricing.setDiscountConfig('crossCategory', {percent: parseFloat(this.value)})">
              </div>
            </div>
          </div>
        </div>

        <!-- Rush Delivery Configuration -->
        <div class="admin-section">
          <h3 class="admin-section-title">⚡ Rush Delivery Settings</h3>
          <div class="admin-toggle" style="margin-bottom: 16px;">
            <label class="toggle-switch">
              <input type="checkbox" id="rush-enabled"
                ${pricingConfig.rushDelivery.enabled ? 'checked' : ''}
                onchange="AdminPricing.setRushEnabled(this.checked)">
              <span class="toggle-slider"></span>
            </label>
            <span>Rush Delivery Enabled</span>
          </div>
          <div class="admin-grid">
            ${Object.entries(pricingConfig.rushDelivery.tiers).map(([key, tier]) => `
              <div class="admin-field">
                <label class="admin-label">${tier.label}</label>
                <div class="admin-toggle">
                  <label class="toggle-switch">
                    <input type="checkbox" 
                      ${tier.enabled ? 'checked' : ''}
                      onchange="AdminPricing.setRushTierEnabled('${key}', this.checked)">
                    <span class="toggle-slider"></span>
                  </label>
                  <input type="number" class="admin-input" 
                    value="${tier.multiplier}"
                    step="0.01"
                    style="width: 80px;"
                    onchange="AdminPricing.setRushTierMultiplier('${key}', parseFloat(this.value))">
                </div>
              </div>
            `).join('')}
          </div>
        </div>

        <!-- Coupon Management -->
        <div class="admin-section">
          <h3 class="admin-section-title">🏷️ Coupon Codes</h3>
          
          <div style="margin-bottom: 20px; display: flex; gap: 12px; flex-wrap: wrap;">
            <input type="text" id="new-coupon-code" class="admin-input" placeholder="Coupon Code" style="width: 150px;">
            <input type="number" id="new-coupon-discount" class="admin-input" placeholder="Discount %" value="5" style="width: 100px;">
            <button class="admin-btn admin-btn-primary" onclick="AdminPricing.createCouponFromUI()">
              + Add Coupon
            </button>
          </div>

          <table class="coupon-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Discount</th>
                <th>Used</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody id="coupon-list">
              ${getAllCoupons().map(coupon => `
                <tr>
                  <td><strong>${coupon.code}</strong></td>
                  <td>${coupon.discount}${coupon.type === 'percent' ? '%' : ' USD'}</td>
                  <td>${coupon.usageCount || 0}${coupon.usageLimit ? `/${coupon.usageLimit}` : ''}</td>
                  <td>
                    <span class="status-badge ${coupon.enabled ? 'status-active' : 'status-inactive'}">
                      ${coupon.enabled ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <button class="admin-btn" onclick="AdminPricing.${coupon.enabled ? 'disableCoupon' : 'enableCoupon'}('${coupon.code}'); AdminPricing.renderAdminPanel('${containerId}');">
                      ${coupon.enabled ? 'Disable' : 'Enable'}
                    </button>
                    <button class="admin-btn admin-btn-danger" onclick="if(confirm('Delete coupon?')) { AdminPricing.deleteCoupon('${coupon.code}'); AdminPricing.renderAdminPanel('${containerId}'); }">
                      Delete
                    </button>
                  </td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>

        <div style="text-align: center; color: #6b7280; font-size: 12px; margin-top: 20px;">
          Last updated: ${pricingConfig.lastUpdated ? new Date(pricingConfig.lastUpdated).toLocaleString() : 'Never'}
        </div>
      </div>
    `;
  }

  function createCouponFromUI() {
    const codeInput = document.getElementById('new-coupon-code');
    const discountInput = document.getElementById('new-coupon-discount');
    
    if (!codeInput?.value) {
      alert('Please enter a coupon code');
      return;
    }
    
    createCoupon(codeInput.value, {
      discount: parseFloat(discountInput?.value) || 5
    });
    
    codeInput.value = '';
    
    // Re-render panel
    const panel = document.querySelector('.admin-pricing-panel')?.parentElement;
    if (panel?.id) {
      renderAdminPanel(panel.id);
    }
  }

  // ==========================================================================
  // PUBLIC API
  // ==========================================================================
  
  window.AdminPricing = {
    init,
    // Service management
    enableService,
    disableService,
    isServiceEnabled,
    setServicePrice,
    getServicePrice,
    clearPriceOverride,
    // Rush delivery
    setRushEnabled,
    setRushTierEnabled,
    setRushTierMultiplier,
    getRushConfig,
    isRushTierEnabled,
    // Discounts
    setDiscountConfig,
    getDiscountConfig,
    getSameCategoryDiscount,
    getCrossCategoryDiscount,
    getMembershipDiscount,
    // Coupons
    createCoupon,
    updateCoupon,
    disableCoupon,
    enableCoupon,
    deleteCoupon,
    getCoupon,
    validateCoupon,
    applyCoupon,
    getAllCoupons,
    getActiveCoupons,
    // UI
    renderAdminPanel,
    createCouponFromUI,
    // Sync
    syncWithBackend,
    pushToBackend,
    // Config
    getConfig: () => ({ ...pricingConfig }),
    CONFIG
  };

  // PricingManager is an alias for the AdminPricing public API
  const PricingManager = window.AdminPricing;

  // Global for backward compatibility
  window.PricingManager = PricingManager;

  // Auto-init
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ES Module exports
  export default PricingManager;
  export { PricingManager };
